import { useState, useEffect, useContext, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { Page } from "pages/Page";
import { RouteLink, RouteList } from "Routes";
import { PageHeader } from "components/PageHeader";
import { GetHallOfFameSearchResult } from "results/hall-of-fame/GetHallOfFameSearchResult";
import { GlobalContext } from "contexts/GlobalContext";
import { useQuery } from "hooks/useQuery";
import { HallOfFameSearchForm, HallOfFameSearchFormData } from "components/hall-of-fame/HallOfFameSearchForm";
import { PaginatedHallOfFameGamesList } from "components/hall-of-fame/PaginatedHallOfFameGamesList";
import { PlaceholderHallOfFameGameCard } from "components/placeholders/PlaceholderHallOfFameGameCard";
import { State } from "country-state-city";

export const HallOfFameSearchParams = {
    query: "q",
    category: "p",
    language: "l",
    country: "c",
    region: "r",
    schoolLevel: "s",
};

const HallOfFameSearchPage = () => {
    const { hallOfFameService, catchServerError, regionNames, regionLanguages } = useContext(GlobalContext);
    const { search } = hallOfFameService;

    const history = useHistory();
    const query = useQuery();

    const [data, setData] = useState<GetHallOfFameSearchResult>();

    // get search parameters from URL
    const searchQuery = decodeURIComponent(query.get(HallOfFameSearchParams.query) || "");
    const language = query.get(HallOfFameSearchParams.language) || undefined;
    const searchCountry = query.get(HallOfFameSearchParams.country) || undefined;
    const region = query.get(HallOfFameSearchParams.region) || undefined;
    const category = query.has(HallOfFameSearchParams.category)
        ? parseInt(query.get(HallOfFameSearchParams.category) || "", 10)
        : undefined;
    const schoolLevel = query.has(HallOfFameSearchParams.schoolLevel)
        ? parseInt(query.get(HallOfFameSearchParams.schoolLevel) || "", 10)
        : undefined;

    const isValidSearch = !!(
        searchQuery.trim().length > 0 ||
        category ||
        language ||
        searchCountry ||
        region ||
        schoolLevel
    );

    // form default values from URL parameters
    const formDefaultValues: HallOfFameSearchFormData = {
        query: searchQuery,
        category: category,
        country: searchCountry,
        region,
        language,
        schoolLevel,
    };

    // search
    useEffect(() => {
        let mounted = true;

        search(searchQuery, category, language, searchCountry, region, schoolLevel)
            .then(x => {
                if (mounted) {
                    setData(x);
                }
            })
            .catch(catchServerError);

        return () => {
            mounted = false;
        };
    }, [search, searchQuery, category, language, searchCountry, region, schoolLevel, catchServerError, isValidSearch]);

    const handleSearch = (formData: HallOfFameSearchFormData) => {
        const url = RouteLink.hallOfFameSearch(formData);

        history.push(url);
    };

    // get the category name from the loaded categories list
    const categoryName = category && data?.categories ? data.categories.find(x => x.id === category)?.name : undefined;

    // get the school level name from the loaded school levels list
    const schoolLevelName =
        schoolLevel && data?.schoolLevels
            ? data.schoolLevels.find(x => x.id === schoolLevel)?.short.toLocaleLowerCase()
            : undefined;

    const searchDescription = useMemo(() => {
        // get display names
        const displayCountry = searchCountry ? regionNames.of(searchCountry || "") || searchCountry : null;
        const displayRegion =
            region && searchCountry ? State.getStateByCodeAndCountry(region, searchCountry)?.name ?? region : null;

        const displayLanguage = language ? regionLanguages.of(language) || language : null;

        // build description
        const descGames = "Games";
        const descQuery = searchQuery ? ` for '${searchQuery}'` : null;
        const descCategory = categoryName ? ` in ${categoryName}` : null;
        const descSchoolLevel = schoolLevelName ? ` from ${schoolLevelName} students` : null;
        const descLanguage = displayLanguage ? ` in ${displayLanguage}` : null;

        const descCountry =
            displayCountry && displayRegion
                ? ` from ${displayRegion}, ${displayCountry}`
                : displayCountry
                ? ` from ${displayCountry}`
                : null;

        return [descGames, descQuery, descCategory, descSchoolLevel, descLanguage, descCountry]
            .filter(Boolean)
            .join("");
    }, [searchCountry, regionNames, region, language, regionLanguages, searchQuery, categoryName, schoolLevelName]);

    const title = "Hall of Fame search";
    const description = isValidSearch ? searchDescription : "Find games in the Hall of Fame";

    const exampleSearches = useMemo(() => {
        const examples: Array<{ label: string; search: HallOfFameSearchFormData }> = [
            { label: "Spooky", search: { query: "spooky" } },
            { label: "Funny", search: { query: "funny" } },
        ];

        // Add up to the first eight categories as examples if categories are loaded
        const sampleCategories = data?.categories?.slice(0, 12) || [];
        sampleCategories.forEach(cat => {
            if (cat) {
                examples.push({ label: cat.name, search: { category: cat.id } });
            }
        });

        return examples;
    }, [data]);

    return (
        <Page title={title} description={description} canonical={RouteList.HallOfFameSearch}>
            <div className="vstack gap-4 gap-lg-5">
                <PageHeader title={title} description={description} icon="🏆" />

                <div className="grid gap-4 gap-lg-5">
                    <div className="g-col-12 g-col-lg-4">
                        <HallOfFameSearchForm
                            countries={data?.countries || []}
                            categories={data?.categories || []}
                            languages={data?.languages || []}
                            schoolLevels={data?.schoolLevels || []}
                            onSubmit={handleSearch}
                            isLoading={!data}
                            defaultValues={formDefaultValues}
                        />
                    </div>

                    <div className="g-col-12 g-col-lg-8">
                        {!isValidSearch ? (
                            <div className="vstack gap-4">
                                <div className="vstack gap-3">
                                    <h4 className="h4">Start exploring</h4>

                                    <div className="lead">
                                        Search the Hall of Fame by a keyword (theme, mood, genre, object, character),
                                        filter by category, school level, language, country or region, or try one of
                                        these quick examples:
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                    {exampleSearches.map(x => (
                                        <Link
                                            key={x.label}
                                            to={RouteLink.hallOfFameSearch(x.search)}
                                            className="btn btn-outline-white btn-sm rounded-pill"
                                        >
                                            {x.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ) : !data ? (
                            <div className="text-center py-5">
                                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <div className="col" key={i}>
                                            <PlaceholderHallOfFameGameCard />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : data ? (
                            <div className="vstack gap-4 gap-lg-5">
                                <PaginatedHallOfFameGamesList games={data.games} />

                                {data.games.length === 0 && (
                                    <div>
                                        <div className="h4">No results found</div>
                                        <p>Try adjusting your search terms or filters</p>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default HallOfFameSearchPage;
